// FILE: src/pages/admin/staff/AdminRoleEditPage.jsx
// Create or change a role (RBAC). The permission catalogue comes from the
// server (GET /api/admin/permissions) as groups of checkboxes, each with a
// plain label and a short explanation. Administrator always has every
// permission and can't be changed or deleted; the other built-in roles keep
// their name and can't be deleted; custom roles can be deleted once nobody
// has them. Changing needs roles.manage; with staff.manage only the page is
// read only. The server's refusals are in words: the name taken, ROLE_IN_USE
// (how many still have it), ROLE_PROTECTED, LAST_ROLE_MANAGER (nobody could
// manage roles afterwards) and permissions you don't hold yourself.
//   GET/PATCH/DELETE /api/admin/roles/:id, POST /api/admin/roles
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Link,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from "../../../components/ui";
import { ConfirmDialog } from "../../../components/admin/ContentBits";
import { errorPayload } from "../../../components/admin/apiError";
import { personName } from "../../../components/admin/format";
import { StaffStatusChip, rbacErrorText, roleProblemText } from "../../../components/admin/staffBits";
import { errorCode, messageFor } from "../../../i18n/errors";
import usePermissions from "../../../hooks/usePermissions";
import { createRole, deleteRole, getPermissionCatalogue, getRole, updateRole } from "../../../services/roles";
import { BuiltInChip, membersText } from "./AdminRolesPage";

const NAME_MAX = 60;
const DESCRIPTION_MAX = 500;
const LIST = "/admin/roles";

export function validateRole(form) {
  const errors = {};
  const name = form.name.trim();
  if (!name) errors.name = "Enter a name for the role.";
  else if (name.length < 2 || name.length > NAME_MAX) errors.name = `Use 2 to ${NAME_MAX} characters for the name.`;
  if (form.description.trim().length > DESCRIPTION_MAX) errors.description = `Use ${DESCRIPTION_MAX} characters or fewer for the description.`;
  return errors;
}

/** One group of the catalogue as a fieldset of checkboxes. */
function PermissionGroup({ group, chosen, onToggle, disabled }) {
  const count = group.permissions.filter((p) => chosen.has(p.key)).length;
  return (
    <Box component="fieldset" sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, m: 0, px: { xs: 2, sm: 2.5 }, pt: 1, pb: 2, minWidth: 0 }}>
      <Typography component="legend" sx={{ fontWeight: 700, px: 0.75 }}>
        {group.label}
      </Typography>
      <Typography sx={{ color: "text.secondary", mb: 1 }}>
        {count} of {group.permissions.length} chosen
      </Typography>
      <Stack spacing={1.25}>
        {group.permissions.map((p) => {
          const descId = `perm-${p.key.replace(/\W/g, "-")}-desc`;
          return (
            <FormControlLabel
              key={p.key}
              sx={{ alignItems: "flex-start", m: 0 }}
              control={
                <Checkbox
                  checked={chosen.has(p.key)}
                  onChange={() => onToggle(p.key)}
                  disabled={disabled}
                  slotProps={{ input: { "aria-describedby": descId } }}
                  sx={{ mt: -0.5 }}
                />
              }
              label={
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600 }}>{p.label}</Typography>
                  <Typography id={descId} sx={{ color: "text.secondary" }}>
                    {p.description}
                  </Typography>
                </Box>
              }
            />
          );
        })}
      </Stack>
    </Box>
  );
}

export default function AdminRoleEditPage({ mode }) {
  const creating = mode === "create";
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { can } = usePermissions();
  const canManage = can("roles.manage");

  const [catalogue, setCatalogue] = useState({ groups: null, error: null });
  const [role, setRole] = useState({ data: null, error: null, loading: !creating });
  const [form, setForm] = useState({ name: "", description: "", permissions: new Set() });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(location.state?.notice || "");
  const [confirm, setConfirm] = useState({ open: false, busy: false, error: null });
  const refs = useRef({});
  const alertRef = useRef(null);

  const loadCatalogue = useCallback(() => {
    setCatalogue({ groups: null, error: null });
    getPermissionCatalogue()
      .then((data) => setCatalogue({ groups: Array.isArray(data?.groups) ? data.groups : [], error: null }))
      .catch((error) => setCatalogue({ groups: null, error: error || "INTERNAL_ERROR" }));
  }, []);

  const fill = (data) =>
    setForm({ name: data.name || "", description: data.description || "", permissions: new Set(data.permissions || []) });

  const loadRole = useCallback(() => {
    if (creating) return;
    setRole({ data: null, error: null, loading: true });
    getRole(id)
      .then((data) => {
        setRole({ data, error: null, loading: false });
        fill(data);
      })
      .catch((error) => setRole({ data: null, error: error || "INTERNAL_ERROR", loading: false }));
  }, [creating, id]);

  useEffect(() => loadCatalogue(), [loadCatalogue]);
  useEffect(() => loadRole(), [loadRole]);

  const current = role.data;
  const administrator = current?.key === "ADMINISTRATOR";
  const builtIn = !!current?.system;
  const readOnly = !canManage || administrator;
  const chosen = useMemo(() => (administrator ? new Set((catalogue.groups || []).flatMap((g) => g.permissions.map((p) => p.key))) : form.permissions), [
    administrator,
    catalogue.groups,
    form.permissions
  ]);

  if (!creating && !current) {
    if (role.loading) return <LoadingState variant="page" label="Loading the role…" />;
    const missing = ["ROLE_NOT_FOUND", "NOT_FOUND"].includes(errorCode(role.error));
    return (
      <Box>
        <PageHeader title={missing ? "Role not found" : "Role"} breadcrumbs={[{ label: "Roles", to: LIST }, { label: "Role" }]} />
        {missing ? (
          <EmptyState
            icon={PersonSearchOutlinedIcon}
            title={messageFor("ROLE_NOT_FOUND")}
            description="It may have been deleted. Choose a role from the list."
            action={
              <Button component={RouterLink} to={LIST} variant="contained">
                Back to roles
              </Button>
            }
          />
        ) : (
          <ErrorState error={role.error} title="We couldn't load this role" onRetry={loadRole} />
        )}
      </Box>
    );
  }

  const title = creating ? "New role" : current.name;
  const toggle = (key) => {
    setForm((prev) => {
      const next = new Set(prev.permissions);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return { ...prev, permissions: next };
    });
    setNotice("");
  };
  const onField = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setNotice("");
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const ordered = () => (catalogue.groups || []).flatMap((g) => g.permissions.map((p) => p.key)).filter((k) => form.permissions.has(k));

  const save = async (e) => {
    e.preventDefault();
    setServerError(null);
    setNotice("");
    const found = validateRole(form);
    setErrors(found);
    if (Object.keys(found).length) {
      refs.current[found.name ? "name" : "description"]?.focus();
      return;
    }
    const body = { description: form.description.trim(), permissions: ordered() };
    if (!builtIn) body.name = form.name.trim();
    setSaving(true);
    try {
      if (creating) {
        const created = await createRole(body);
        navigate(`${LIST}/${created.id}`, { replace: true, state: { notice: `“${created.name}” was created. Choose it for staff on their edit page.` } });
        return;
      }
      const updated = await updateRole(current.id, body);
      setRole((r) => ({ ...r, data: { ...r.data, ...updated } }));
      fill(updated);
      setNotice("Changes saved. They apply to everyone with this role straight away.");
    } catch (err) {
      setServerError(err || "INTERNAL_ERROR");
      const problems = errorPayload(err).problems || [];
      const fields = {};
      for (const p of problems) {
        if (p.field === "name" || p.field === "description") fields[p.field] = roleProblemText(p);
      }
      setErrors((prev) => ({ ...prev, ...fields }));
      setTimeout(() => (fields.name ? refs.current.name?.focus() : alertRef.current?.focus()), 0);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setConfirm((c) => ({ ...c, busy: true, error: null }));
    try {
      await deleteRole(current.id);
      navigate(LIST, { state: { notice: `“${current.name}” was deleted.` } });
    } catch (err) {
      setConfirm({ open: true, busy: false, error: err || "INTERNAL_ERROR" });
    }
  };

  const problemList = (errorPayload(serverError).problems || []).filter((p) => p.field !== "name" && p.field !== "description");
  const members = Array.isArray(current?.memberList) ? current.memberList : [];

  return (
    <Box>
      <PageHeader
        eyebrow={creating ? "Roles" : "Role"}
        title={title}
        breadcrumbs={[{ label: "Roles", to: LIST }, { label: title }]}
        description={
          creating
            ? "Name the role, say what it's for and choose what it may do. Give it to staff from their edit page."
            : current.description || undefined
        }
        actions={
          !creating && canManage && !builtIn ? (
            <Button variant="outlined" color="error" startIcon={<DeleteOutlineIcon />} onClick={() => setConfirm({ open: true, busy: false, error: null })}>
              Delete role
            </Button>
          ) : undefined
        }
      >
        {!creating && (
          <Stack direction="row" spacing={1.5} sx={{ mt: 1.5, alignItems: "center", flexWrap: "wrap", rowGap: 1 }}>
            <BuiltInChip role={current} />
            <Typography sx={{ color: "text.secondary" }}>{membersText(current.members)}</Typography>
          </Stack>
        )}
      </PageHeader>

      {notice && (
        <Alert severity="success" role="status" sx={{ mb: 2.5 }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}
      {administrator ? (
        <Alert severity="info" sx={{ mb: 2.5 }} data-testid="administrator-note">
          Administrator has every permission, including managing staff and roles, and always will. It can't be changed or
          deleted.
        </Alert>
      ) : builtIn ? (
        <Alert severity="info" sx={{ mb: 2.5 }}>
          A built-in role: you can change what it may do and its description, but not its name, and it can't be deleted.
        </Alert>
      ) : null}
      {!canManage && !administrator && (
        <Alert severity="info" sx={{ mb: 2.5 }}>
          You can see this role but not change it. Changing roles needs the “Manage roles” permission.
        </Alert>
      )}

      <Box component="form" noValidate onSubmit={save} aria-label={creating ? "New role" : `Role ${title}`}>
        <Stack spacing={2.5}>
          {serverError && (
            <Alert severity="error" ref={alertRef} tabIndex={-1} role="alert">
              <AlertTitle>We couldn't save the role</AlertTitle>
              {rbacErrorText(serverError)}
              {problemList.length > 0 && <Box sx={{ mt: 1 }}>{roleProblemText(problemList[0])}</Box>}
            </Alert>
          )}

          <Section id="role-details" title="Name and description">
            <Stack spacing={2.5}>
              <TextField
                id="role-name"
                name="name"
                label="Name"
                value={form.name}
                onChange={onField}
                inputRef={(el) => (refs.current.name = el)}
                error={!!errors.name}
                helperText={errors.name || (builtIn ? "Built-in roles keep their name." : `2 to ${NAME_MAX} characters, different from the other roles.`)}
                disabled={readOnly || builtIn}
                slotProps={{ htmlInput: { maxLength: NAME_MAX } }}
                required
                fullWidth
              />
              <TextField
                id="role-description"
                name="description"
                label="What it's for"
                value={form.description}
                onChange={onField}
                inputRef={(el) => (refs.current.description = el)}
                error={!!errors.description}
                helperText={errors.description || "Optional. Shown when someone chooses a role for a staff member."}
                disabled={readOnly}
                slotProps={{ htmlInput: { maxLength: DESCRIPTION_MAX } }}
                multiline
                minRows={2}
                fullWidth
              />
            </Stack>
          </Section>

          <Section
            id="role-permissions"
            title="What this role may do"
            description={
              administrator
                ? "Every permission, now and in future."
                : `${chosen.size} ${chosen.size === 1 ? "permission" : "permissions"} chosen. Staff see only the pages and buttons their role allows, and the server refuses everything else.`
            }
          >
            {catalogue.error ? (
              <ErrorState compact error={catalogue.error} title="We couldn't load the permissions" onRetry={loadCatalogue} />
            ) : catalogue.groups === null ? (
              <LoadingState variant="inline" label="Loading the permissions…" />
            ) : (
              <Stack spacing={2}>
                {catalogue.groups.map((g) => (
                  <PermissionGroup key={g.id} group={g} chosen={chosen} onToggle={toggle} disabled={readOnly} />
                ))}
              </Stack>
            )}
          </Section>

          {!readOnly && (
            <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5} sx={{ justifyContent: "flex-end" }}>
              <Button component={RouterLink} to={LIST} variant="outlined">
                Back to roles
              </Button>
              <Button type="submit" variant="contained" startIcon={<SaveOutlinedIcon />} disabled={saving || catalogue.groups === null}>
                {saving ? "Saving…" : creating ? "Create role" : "Save changes"}
              </Button>
            </Stack>
          )}
        </Stack>
      </Box>

      {!creating && (
        <Section id="role-members" title="Staff with this role" sx={{ mt: 2.5 }}>
          {members.length === 0 ? (
            <Typography sx={{ color: "text.secondary" }}>Nobody has this role yet. Choose it on a staff member's edit page.</Typography>
          ) : (
            <Stack component="ul" spacing={1.5} sx={{ listStyle: "none", m: 0, p: 0 }}>
              {members.map((m) => (
                <Stack component="li" key={m._id} direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ alignItems: { sm: "center" } }}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Link component={RouterLink} to={`/admin/users/${m._id}`} sx={{ fontWeight: 600 }}>
                      {personName(m, m.email)}
                    </Link>
                    <Typography sx={{ color: "text.secondary", overflowWrap: "anywhere" }}>{m.email}</Typography>
                  </Box>
                  <Box>
                    <StaffStatusChip user={m} />
                  </Box>
                </Stack>
              ))}
            </Stack>
          )}
        </Section>
      )}

      {!creating && (
        <ConfirmDialog
          open={confirm.open}
          id="delete-role"
          title={`Delete “${current.name}”?`}
          icon={DeleteOutlineIcon}
          tone="error"
          description="The role is removed for good. Staff can't have a role that's deleted, so give them another role first."
          confirmLabel="Delete role"
          busyLabel="Deleting…"
          confirmColor="error"
          busy={confirm.busy}
          error={confirm.error}
          errorDetail={confirm.error && errorCode(confirm.error) === "ROLE_IN_USE" ? rbacErrorText(confirm.error) : null}
          onConfirm={remove}
          onClose={() => setConfirm({ open: false, busy: false, error: null })}
        />
      )}
    </Box>
  );
}
